/**
 * MISSION DEBRIEFING.
 *
 * The field list is `PS.EXE`'s own, verbatim and in its own order
 * (docs/game/ui-menus.md § *Mission Debriefing*): `MISSION SCORE`,
 * `AIRCRAFT LOST`, `VICTORIES` split `PERSONAL` / `SQUADRON`, then the kill
 * categories `AIRCRAFT`, `BALLOONS`, `ZEPPELINS`, then `CONTINUE`. This module
 * fills those fields in; the screen that draws them is the app's business.
 *
 * The **scoring formula** is port-side and marked as such. `PS.EXE` shows a
 * MISSION SCORE but nothing this project has decoded says how it is computed,
 * and inventing a number and calling it the original's would be exactly the
 * kind of unearned provenance the docs warn about. What is below is a plain,
 * legible weighting — victories are worth much more than accuracy, a Zeppelin
 * is worth more than an aeroplane, and failing the mission costs you the
 * bonus — so a player can see why the number moved.
 */

import type { VictoryRecord } from "./kill-credit.ts";
import type { MissionOutcome, MissionRun, MissionStats } from "./mission-run.ts";
import { objectiveProgressLabel, type MissionObjective, type ObjectiveState } from "./objectives.ts";
import { raidSummaryLine, type RaidOutcome } from "./raid.ts";
import { reviewNarrative } from "./debrief-review.ts";
import { reviewFactsFrom } from "./debrief-review-facts.ts";

/** Port-side point values. See the module note. */
export const SCORE_VALUES = {
  aircraftKill: 100,
  balloonKill: 150,
  zeppelinKill: 400,
  missionSuccess: 250,
  friendlyLoss: -75,
  ownAircraftLost: -150,
  perHitScored: 1,
  /**
   * The one score component the manual actually names: *"Bonus points for
   * landing at your own aerodrome upon completion of your mission"* and *"you
   * will receive more points for landing at your aerodrome"* (04-gameplay.md).
   * Paid only on a *successful* mission, which is what "upon completion" says.
   * The size of it is ours, like every other number here.
   */
  landedAtBase: 150,
  /** An aeroplane written off in the landing is still an aeroplane lost, if less badly than one shot down. */
  gearCollapsed: -75,
  /**
   * A raid target levelled, and one of your own lost. The manual's scoring list
   * (docs/game/manual/04-gameplay.md) names aircraft, balloons and Zeppelins and
   * says nothing about ground targets, so both of these are port-side like the
   * rest of this table — sized so that letting a raid through hurts about as
   * much as losing a wingman.
   */
  groundTargetDestroyed: 120,
  friendlyGroundTargetLost: -90,
} as const;

export interface ObjectiveReport {
  readonly label: string;
  readonly status: ObjectiveState["status"];
  /** `"2 / 3"`, or empty for a yes/no objective. */
  readonly progress: string;
  readonly primary: boolean;
}

export interface MissionDebrief {
  /** The mission this is a debriefing of, so the record stands on its own. */
  readonly missionId: string;
  readonly title: string;
  readonly outcome: MissionOutcome;
  /** The line across the top, in the sim's own voice. */
  readonly headline: string;
  /**
   * The paragraph the original's MISSION DEBRIEFING screen draws above the
   * score plate — one of `REVIEW.DAT`'s 52 pre-written messages, as its stored
   * runs (`debrief-review.ts`). **Join these with a space and wrap to the
   * panel**: they are string-pool boundaries, not screen rows, and the original
   * re-flows the paragraph (see `review-dat.ts`'s module note).
   *
   * `headline` above is port-side and stays for the callers that want one
   * short string; this is what the authentic screen shows.
   */
  readonly narrative: readonly string[];
  readonly missionScore: number;
  readonly aircraftLost: number;
  readonly personalVictories: number;
  readonly squadronVictories: number;
  /** The `SQUADRON` column's other two rows — see `KillTally.squadronBalloonKills`. */
  readonly squadronBalloonVictories: number;
  readonly squadronZeppelinVictories: number;
  readonly aircraftKills: number;
  readonly balloonKills: number;
  readonly zeppelinKills: number;
  readonly roundsFired: number;
  readonly hitsScored: number;
  readonly hitsTaken: number;
  /** `"4:12"` — how long the sortie took. */
  readonly duration: string;
  /**
   * One entry per kill, with the two facts a career needs to confirm it. The
   * original's debriefing screen had no such list; a campaign cannot credit
   * victories honestly without one.
   */
  readonly victories: readonly VictoryRecord[];
  readonly friendlyKills: number;
  readonly groundTargetsDestroyed: number;
  readonly friendlyGroundTargetsLost: number;
  /**
   * How many of those the player bombed himself. Reported beside the raid's
   * total rather than in `personalVictories`: a shed is not a victory, and
   * `PS.EXE`'s screen has no column for one (see `MissionStats`).
   */
  readonly playerGroundTargetsDestroyed: number;
  /**
   * Enemy ships the player put under — the one field on this record that the
   * original's screen has no counterpart for, and it is **declared port-side
   * invention behind ENHANCED SIMULATION** rather than a reading of anything
   * (`docs/engineering/completeness-inventory.md`'s I-row, `ship-sim.md` §5b.7).
   *
   * The user asked for it on 2026-09-01 after the warfare lane had refused to
   * decide it: `PS.EXE` has three victory columns and a ship is in none of them,
   * so the honest place for her was a new row on the port's own side of the
   * screen. `debrief-screens.ts` draws that row **only when this is above zero**,
   * so a sortie with the setting off — or on with nothing sunk — leaves the
   * screen pixel-identical to the capture it was measured against.
   *
   * Nothing else reads it: it is not a `VictoryKind`, it is not in `victories`,
   * `missionScore` does not pay for it and `applyDebrief` does not credit it.
   *
   * **Additive**, on the footing `baledOut` and `killedBy` are: a career save
   * written before this simply has no such key, which every reader below treats
   * as the zero it is, so `CAREER_SAVE_VERSION` does not move.
   */
  readonly shipsSunk: number;
  /** Present only on the raid family; the card draws its own block when it is. */
  readonly raid: RaidOutcome | undefined;
  readonly playerLost: boolean;
  readonly playerWounded: boolean;
  readonly pilotKilled: boolean;
  readonly overFriendlyTerritory: boolean;
  readonly landedSafely: boolean;
  readonly landedAtBase: boolean;
  readonly gearCollapsed: boolean;
  /**
   * Down in one piece on the wrong side of the wire. The manual makes this a
   * distinct ending from a crash: *"If you **land** or crash (and survive) in
   * enemy territory, you may end up in an enemy prisoner-of-war camp."*
   */
  readonly landedInEnemyTerritory: boolean;
  /**
   * `SIMEND.DAT`'s `%a` — *"%a has shot you down."* — as `MissionStats.killedBy`
   * resolved it, or `undefined` when the port cannot honestly name a person.
   * This is what makes `VANQUISHED!`'s named page reachable at all; it is also
   * the name the career's death notice wants.
   *
   * **Additive and optional on purpose.** A career save written before this
   * existed simply has `undefined` here, and `simendOutcome` already falls back
   * to the unnamed `VANQUISHED!` in that case — so no save version moves.
   */
  readonly killedBy: string | undefined;
  /**
   * The pilot went over the side and came down under a canopy (AD-021).
   *
   * **Additive and optional in effect**, on the same footing `killedBy` is: it
   * is `false` for every sortie flown before the feature and for every sortie
   * flown with it switched off, and `simendOutcome` reads it as one more `false`
   * predicate, so no career save and no screen had to move.
   */
  readonly baledOut: boolean;
  readonly objectives: readonly ObjectiveReport[];
  /** One or two sentences summarising how it went, for the top of the card. */
  readonly summary: readonly string[];
}

export function missionScore(stats: MissionStats, outcome: MissionOutcome): number {
  const values = SCORE_VALUES;
  const kills =
    stats.aircraftKills * values.aircraftKill +
    stats.balloonKills * values.balloonKill +
    stats.zeppelinKills * values.zeppelinKill;
  const losses =
    stats.friendlyLosses * values.friendlyLoss +
    stats.friendlyGroundTargetsLost * values.friendlyGroundTargetLost;
  const own = stats.playerStatus === "destroyed" ? values.ownAircraftLost : 0;
  const bonus = outcome === "success" ? values.missionSuccess : 0;
  const home = stats.landedAtBase && outcome === "success" ? values.landedAtBase : 0;
  const wrecked = stats.gearCollapsed && stats.playerStatus !== "destroyed" ? values.gearCollapsed : 0;
  const ground = stats.groundTargetsDestroyed * values.groundTargetDestroyed;
  return kills + losses + own + bonus + home + wrecked + ground + stats.hitsScored * values.perHitScored;
}

export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * The headline. `MISSION ACCOMPLISHED` / `MISSION FAILED` are the plainest
 * reading of a screen that shows a MISSION SCORE and a pass/fail outcome; the
 * shot-down line borrows the sim's own `"You've been shot down"` register.
 */
export function debriefHeadline(outcome: MissionOutcome, stats: MissionStats): string {
  if (outcome === "success") return "MISSION ACCOMPLISHED";
  if (stats.playerStatus === "destroyed") return "YOU DID NOT RETURN";
  if (outcome === "failure") return "MISSION FAILED";
  return "MISSION BROKEN OFF";
}

function objectiveReport(objective: MissionObjective, state: ObjectiveState | undefined): ObjectiveReport {
  return {
    label: objective.label,
    status: state?.status ?? "pending",
    progress: state ? objectiveProgressLabel(state) : "",
    primary: objective.role === "primary",
  };
}

/** The line the landing earns, if any — the manual's three endings, in its own order of consequence. */
function landingLine(stats: MissionStats): string | undefined {
  if (stats.playerStatus === "destroyed") return undefined;
  if (stats.gearCollapsed) return "You put it down hard and the undercarriage went. The machine is a write-off.";
  if (!stats.landedSafely) return undefined;
  if (!stats.overFriendlyTerritory) return "You came down on the wrong side of the lines.";
  return stats.landedAtBase ? "You landed back at your own aerodrome." : "You landed in a field on our side of the lines.";
}

function summaryLines(run: MissionRun, stats: MissionStats): string[] {
  const lines: string[] = [];
  const victories = stats.aircraftKills + stats.balloonKills + stats.zeppelinKills;
  if (victories === 0) lines.push("No victories claimed.");
  else if (victories === 1) lines.push("One victory confirmed.");
  else lines.push(`${victories} victories confirmed.`);
  const landing = landingLine(stats);
  if (landing) lines.push(landing);
  if (stats.raid) lines.push(raidSummaryLine(stats.raid));
  // Said in the summary rather than added to a victory column, for the reason
  // `MissionStats.playerGroundTargetsDestroyed` gives: the original's debriefing
  // has no line for a shed, so the player is told plainly instead of credited
  // with a kill he did not score.
  if (stats.playerGroundTargetsDestroyed > 0) {
    lines.push(
      stats.playerGroundTargetsDestroyed === 1
        ? "One of those was yours — your bombs went in."
        : `${stats.playerGroundTargetsDestroyed} of those were yours — your bombs went in.`,
    );
  }
  if (stats.playerWounded) lines.push("You were wounded and will be off flying duty.");
  if (stats.friendlyLosses > 0) {
    lines.push(stats.friendlyLosses === 1 ? "One of ours did not come back." : `${stats.friendlyLosses} of ours did not come back.`);
  }
  const failed = run.objectives.find((state) => state.status === "failed");
  if (failed) {
    const objective = run.spec.objectives.find((entry) => entry.id === failed.id);
    if (objective) lines.push(`Objective lost: ${objective.label.toLowerCase()}.`);
  }
  return lines;
}

/**
 * He jumped and he is alive on the ground. The mission layer has already
 * repointed `pilotKilled` at the canopy rather than the seat when there is one
 * (`mission-run.ts`'s `bailingOf`), so this is the whole test.
 */
function bailedDownAlive(stats: MissionStats): boolean {
  return stats.baledOut && !stats.pilotKilled;
}

export function missionDebrief(run: MissionRun): MissionDebrief {
  const { stats } = run;
  return {
    missionId: run.spec.id,
    title: run.spec.title,
    outcome: run.outcome,
    headline: debriefHeadline(run.outcome, stats),
    narrative: reviewNarrative(run.spec.type, reviewFactsFrom(run)),
    missionScore: missionScore(stats, run.outcome),
    aircraftLost: stats.friendlyLosses,
    personalVictories: stats.aircraftKills + stats.balloonKills + stats.zeppelinKills,
    squadronVictories: stats.squadronKills,
    squadronBalloonVictories: stats.squadronBalloonKills,
    squadronZeppelinVictories: stats.squadronZeppelinKills,
    aircraftKills: stats.aircraftKills,
    balloonKills: stats.balloonKills,
    zeppelinKills: stats.zeppelinKills,
    roundsFired: stats.roundsFired,
    hitsScored: stats.hitsScored,
    hitsTaken: stats.hitsTaken,
    duration: formatDuration(stats.durationS),
    victories: stats.victories,
    friendlyKills: stats.friendlyKills,
    groundTargetsDestroyed: stats.groundTargetsDestroyed,
    friendlyGroundTargetsLost: stats.friendlyGroundTargetsLost,
    playerGroundTargetsDestroyed: stats.playerGroundTargetsDestroyed,
    shipsSunk: stats.shipsSunk,
    raid: stats.raid,
    playerLost: stats.playerStatus === "destroyed",
    playerWounded: stats.playerWounded,
    pilotKilled: stats.pilotKilled,
    overFriendlyTerritory: stats.overFriendlyTerritory,
    landedSafely: stats.landedSafely,
    landedAtBase: stats.landedAtBase,
    gearCollapsed: stats.gearCollapsed,
    // A bailed pilot who reaches the ground alive on the wrong side is down in
    // one piece in enemy territory, which is exactly what this predicate names —
    // `stats.landedSafely` is the *aeroplane's* undercarriage and says nothing
    // about him, so the bail is the second way to be in this state.
    landedInEnemyTerritory: (stats.landedSafely || bailedDownAlive(stats)) && !stats.overFriendlyTerritory,
    killedBy: stats.killedBy,
    baledOut: stats.baledOut,
    objectives: run.spec.objectives.map((objective) =>
      objectiveReport(objective, run.objectives.find((state) => state.id === objective.id)),
    ),
    summary: summaryLines(run, stats),
  };
}
